import { Component, OnInit } from '@angular/core';
import { finalize, Observable, Subject, switchMap, filter, takeUntil, tap, debounceTime, distinctUntilChanged } from 'rxjs';
import { ViewChild, ChangeDetectorRef } from '@angular/core';
import { KnBrRequestQuoteDetailsComponent } from './kn-br-request-quote-details/kn-br-request-quote-details.component';
import { GlobalMessageService, GlobalMessageType, RoutingService } from '@spartacus/core';
import { ActiveCartService } from '@spartacus/cart/base/core';
import { MultiCartFacade } from '@spartacus/cart/base/root';
import { KnBrCartContextService } from 'src/app/store/kn-br-cart-context/kn-br-cart-context.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IconComponent } from '@spartacus/storefront';
import { KnBrRequestQuoteReviewComponent } from './kn-br-request-quote-review/kn-br-request-quote-review.component';
import { KnBrSalesCheckoutService } from 'src/app/services/kn-br-sales-checkout.service';
import { KnBrCommonService } from 'src/app/services/kn-br-common.service';


@Component({
  standalone: true,
  selector: 'kn-br-request-quote',
  templateUrl: './kn-br-request-quote.component.html',
  styleUrls: ['./kn-br-request-quote.component.scss'],
  imports: [CommonModule, IconComponent, FormsModule, KnBrRequestQuoteDetailsComponent, KnBrRequestQuoteReviewComponent],

})
export class KnBrRequestQuoteComponent implements OnInit {

 currentStep = 1;
  shippingSubmitted = false;
  private destroy$ = new Subject<void>();
  poNumber: string = '';
  poNumberError = '';
  cartId: string = "";
  totalPrice: any = null;
  orderemail:string="";

  // ✓ Subject for debounced PO number updates
  private poNumberSubject$ = new Subject<string>();

  @ViewChild(KnBrRequestQuoteDetailsComponent)
  shippingDetailsComponent!: KnBrRequestQuoteDetailsComponent;

  constructor(private KnBrCheckoutService: KnBrSalesCheckoutService,
    protected globalMessageService: GlobalMessageService,
    private routingService: RoutingService,
    protected commonService: KnBrCommonService,
    protected activeCartService: ActiveCartService,
    private multiCartFacade: MultiCartFacade,
    private knBrCartContextService: KnBrCartContextService,
    private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // ✓ Setup debounced PO Number updates (500ms delay)
    this.poNumberSubject$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((poNumber) => {
        this.savePONumber(poNumber);
      });

    this.knBrCartContextService.get$
      .pipe(
        takeUntil(this.destroy$),
        filter((cartId) => !!cartId),
        tap((cartId) => {
          this.cartId = cartId;
          this.multiCartFacade.reloadCart(cartId);
        }),
        switchMap((cartId) => this.multiCartFacade.getCart(cartId)),
        filter((cart) => !!cart)
      )
      .subscribe(
        (cart) => {
          this.poNumber = cart.purchaseOrderNumber || '';
          this.totalPrice = cart?.totalPrice || null;
          this.orderemail = cart?.deliveryAddress?.email || '';
          this.cdr.detectChanges();
        },
        (error) => {
          console.error('Error loading cart in checkout:', error);
        }
      );
  }

  goToStep(step: number) {
    if (step === 1) {
      this.shippingSubmitted = false;
      this.currentStep = 1;
      return;
    }

    if (step === 2 && this.canOpenReview()) {
      this.currentStep = 2;
    }
  }

  togglePanel(event: Event, step: number) {
    event.stopPropagation();
    if (this.currentStep === step) {
      this.currentStep = 0;
    } else {
      this.goToStep(step);
    }
  }

  submitShippingDetails() {
    if (this.shippingDetailsComponent.shippingForm.invalid) {
      this.shippingDetailsComponent.shippingForm.markAllAsTouched();
      this.currentStep = 1;
      return;
    }
    else {
      this.commonService.showSpinner$.next(true);
      const { submitterEmail, firstName, lastName, company, street1, street2, city, state, postal, country, phone, plantLocation, comments } = this.shippingDetailsComponent.shippingForm.value;
      const cleanedPhoneNumber = String(phone || '').replace(/\D/g, "");
      const plantLocationName = this.shippingDetailsComponent.labelsPlantName;
      const multipleShipment = this.shippingDetailsComponent.shippingForm.value.multipleShipment;
      const shippingPayload = {
        email: submitterEmail,
        firstName: firstName,
        lastName: lastName,
        companyName: company,
        line1: street1,
        line2: street2,
        town: city,
        region: {
          isocode: state
        },
        postalCode: postal,
        country: {
          isocode: country
        },
        phone: cleanedPhoneNumber,
        labelsPlantId : plantLocation,
        note: comments,
        labelsPlantName:plantLocationName,
        multipleShipment: multipleShipment
      }

      this.KnBrCheckoutService.shippingDetails(shippingPayload,true)
        .pipe(
          finalize(() => {
            this.commonService.showSpinner$.next(false);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe({
            next: () => {
              this.shippingSubmitted = true;
              this.multiCartFacade.reloadCart(this.cartId);
              if (this.currentStep === 1) {
                this.currentStep = 2;
                this.cdr.detectChanges();
                window.scrollTo(0, 0);
              }
              this.globalMessageService.add(
                'Shipping details updated successfully.',
                GlobalMessageType.MSG_TYPE_CONFIRMATION
              );
            },
            error: () => {
              this.shippingSubmitted = false;
              this.globalMessageService.add(
                'Failed to submit Shipping Details.',
                GlobalMessageType.MSG_TYPE_ERROR
              );
            }
          });
    }
  }

  editShipping(event: Event): void {
    event.stopPropagation();
    this.shippingSubmitted = false;
    this.currentStep = 1;
  }

  previous() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  get canPlaceOrder(): boolean {
    return this.shippingSubmitted && this.shippingDetailsComponent?.shippingForm.valid === true;
  }

  onPoNumberInput(event: Event): void {
    this.poNumber = (event.target as HTMLInputElement).value;
    this.poNumberError = '';
    this.cdr.detectChanges();
    // ✓ Emit to subject (will be debounced)
    this.poNumberSubject$.next(this.poNumber);
  }

  private canOpenReview(): boolean {
    const shippingForm = this.shippingDetailsComponent.shippingForm;
    if (shippingForm.invalid) {
      shippingForm.markAllAsTouched();
      this.globalMessageService.add(
        'Please submit Shipping Details.',
        GlobalMessageType.MSG_TYPE_WARNING
      );
      this.currentStep = 1;
      return false;
    }
    else if (!this.shippingSubmitted) {
      this.globalMessageService.add(
        'Please submit Shipping Details.',
        GlobalMessageType.MSG_TYPE_WARNING
      );
      this.currentStep = 1;
      return false;
    }
    else {
      return true;
    }
  }

  savePONumber(poNumber?: string): void {
    // ✓ Use passed parameter or current value
    const valueToSave = poNumber !== undefined ? poNumber : this.poNumber;
    const trimmedPONumber = valueToSave?.trim() || '';
    this.commonService.showSpinner$.next(true);
    this.KnBrCheckoutService.updatePONumber(trimmedPONumber).subscribe({
      next: () => {
        this.commonService.showSpinner$.next(false);
        this.poNumber = trimmedPONumber;
        this.poNumberError = '';
      },
      error: (error) => {
        this.commonService.showSpinner$.next(false);
        this.poNumberError = error?.error?.errors?.[0]?.message || 'Unable to save PO/Job Number.';
      }
    });
  }

  placeOrder(): void {
    if (!this.canPlaceOrder) {
      this.globalMessageService.add(
        'Please complete the shipping details before placing the order.',
        GlobalMessageType.MSG_TYPE_ERROR
      );
      return;
    }
    this.commonService.showSpinner$.next(true);
    this.KnBrCheckoutService.placeOrderRFQ()
      .pipe(
        finalize(() => {
          this.commonService.showSpinner$.next(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data: any) => {
          setTimeout(() => {
            this.globalMessageService.add(
              'Quote submitted successfully.',
              GlobalMessageType.MSG_TYPE_CONFIRMATION
            );
            this.KnBrCheckoutService.setOrderConfirmData(data);
            this.knBrCartContextService.reset();
            this.routingService.goByUrl('/requestQuoteConfirmation');
          }, 1000);
        },
        error: () => {
          this.globalMessageService.add(
            'Failed to submit Quote.',
            GlobalMessageType.MSG_TYPE_ERROR
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
